import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

export type LoadingIndicatorPlacement = 'overlay' | 'end';

export type LoadableComponentProps = {
    /** Controls whether the loading indicator is visible. */
    loading: boolean;
    /** The interactive element to render (e.g. Button, Switch, Checkbox...). */
    children: React.ReactElement;
    /** Disable the wrapped control while loading (default: true). */
    disableWhileLoading?: boolean;
    /** Placement for the loading indicator relative to the control (default: overlay). */
    indicatorPlacement?: LoadingIndicatorPlacement;
    /** Optional class name on the wrapper. */
    className?: string;
    /** Optional style overrides for the wrapper. */
    style?: React.CSSProperties;
};

const LoadableComponent: React.FC<LoadableComponentProps> = ({
    loading,
    children,
    disableWhileLoading = true,
    indicatorPlacement = 'overlay',
    className,
    style,
}) => {
    let control: React.ReactNode = children;

    if (React.isValidElement(children)) {
        const child = children as React.ReactElement<any>;
        const extraProps: Record<string, unknown> = {};

        if (loading && disableWhileLoading) {
            extraProps.disabled = true;
        }

        if (indicatorPlacement === 'overlay' && loading) {
            const currentStyle: React.CSSProperties = child.props.style ?? {};
            extraProps.style = {
                ...currentStyle,
                pointerEvents: currentStyle.pointerEvents ?? 'none',
                opacity: currentStyle.opacity ?? 0.6,
            } satisfies React.CSSProperties;
        }

        control = Object.keys(extraProps).length > 0 ? React.cloneElement(child, extraProps) : child;
    }

    if (indicatorPlacement === 'end') {
        return (
            <Box className={className} style={style} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {control}
                <CircularProgress size={18} thickness={5} sx={{ visibility: loading ? 'visible' : 'hidden' }} />
            </Box>
        );
    }

    return (
        <Box
            className={className}
            style={style}
            sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {control}
            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <CircularProgress size={22} thickness={5} />
                </Box>
            )}
        </Box>
    );
};

export default LoadableComponent;
